/**
 * Email gate for a quiz slide.
 *
 * The carousel in base.js already refuses to advance while the active slide holds
 * no checked input, and re-checks that on every change event the quiz form emits.
 * Rather than duplicate or override that, this element drives a hidden checkbox:
 * the slide is marked data-multiple-choice, so base.js requires a checked box, and
 * the box is only checked once an address has been accepted. No change to base.js.
 *
 * The address is handed to the real Shopify customer form that sections/quiz.liquid
 * renders next to the quiz — hidden, and outside the quiz's own GET form, because a
 * nested form element is discarded by the browser and the address would be handed to
 * the collection URL instead.
 *
 * That form is submitted through requestSubmit(), not fetch and not submit(): the
 * shop runs Shopify's spam protection, and /contact answers 400 to anything carrying
 * no hCaptcha token. Only a real submit event lets Shopify's captcha-bootstrap script
 * inject the form_key and the token — fetch never fires one, and form.submit() skips
 * it. The form also carries data-shopify-captcha so hCaptcha binds to it at load:
 * binding otherwise waits for a focusin or change inside the form, which never comes
 * for a hidden one. Targeting a hidden iframe absorbs the navigation so the quiz
 * stays put.
 */
class QuizEmailCapture extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-email-input]');
    this.gate = this.querySelector('[data-email-gate]');
    this.button = this.querySelector('[data-email-submit]');
    this.errorEl = this.querySelector('[data-email-error]');
    if (!this.input || !this.gate || !this.button) return;

    this.button.addEventListener('click', this.submit.bind(this));
    this.input.addEventListener('input', this.clearError.bind(this));
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      // The quiz form is a GET form; Enter would submit it and leave for the
      // collection URL mid-quiz.
      event.preventDefault();
      this.submit();
    });
  }

  clearError() {
    this.errorEl?.classList.add('d-none');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('d-none');
  }

  async submit() {
    if (this.pending) return;

    const email = this.input.value.trim();
    if (!email || !this.input.checkValidity()) {
      this.showError(this.dataset.error || this.input.validationMessage);
      this.input.focus();
      return;
    }

    this.clearError();
    this.pending = true;
    this.button.classList.add('loadingState');
    this.button.setAttribute('disabled', 'disabled');

    await this.subscribe(email);

    this.pending = false;
    this.button.classList.remove('loadingState');
    this.button.removeAttribute('disabled');
    this.openGate();
    this.advance();
  }

  /**
   * Failure never blocks the customer: a rejected or unreachable request still lets
   * the quiz continue, because the address has been given either way and stranding
   * someone on this slide over a network error is the worse outcome. Every failure
   * path logs, so a signup that silently never lands is visible in the console.
   */
  async subscribe(email) {
    // Scoped to this quiz first; the document-wide fallback keeps a single stray quiz
    // working if the markup ever moves out of <quiz-form>.
    const container =
      this.closest('quiz-form')?.querySelector('[data-quiz-customer-form]') ||
      document.querySelector('[data-quiz-customer-form]');
    const form = container?.querySelector('form');
    const sink = container?.querySelector('[data-quiz-customer-sink]');
    const emailField = form?.querySelector('[data-quiz-customer-email]');
    if (!form || !sink || !emailField) {
      console.warn(
        'Quiz email capture: hidden customer form missing, address not saved. Check that sections/quiz.liquid is deployed.'
      );
      return;
    }

    emailField.value = email;
    const tagsField = form.querySelector('[data-quiz-customer-tags]');
    if (tagsField && this.dataset.tags) tagsField.value = this.dataset.tags;

    form.target = sink.name;
    const landed = this.waitForSink(sink);
    // requestSubmit() and not submit(): only the former fires the submit event that
    // Shopify's captcha script listens for. It prevents that first submit, injects the
    // form_key and the hCaptcha token, then submits the form itself.
    form.requestSubmit();
    await landed;
    emailField.value = '';

    // No success check here: Shopify sends frame-ancestors 'none' on storefront pages,
    // so the response cannot render in the iframe and its URL cannot be read. The POST
    // still reaches Shopify — the CSP notice the browser logs is about the discarded
    // response, not the request. Confirm signups in Admin > Customers, tag 'quiz'.
  }

  /**
   * Resolves on the iframe's load, or after a timeout so a hung request never keeps
   * the customer waiting on this slide.
   */
  waitForSink(sink) {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        sink.removeEventListener('load', done);
        resolve();
      };
      const timer = setTimeout(done, 5000);
      sink.addEventListener('load', done);
    });
  }

  openGate() {
    if (this.gate.checked) return;
    this.gate.checked = true;
    // Setting .checked fires nothing on its own, and the change event is what tells
    // base.js to re-evaluate the next button.
    this.gate.dispatchEvent(new Event('change', { bubbles: true }));
  }

  advance() {
    // Reuses the footer button so the last slide still submits the quiz rather than
    // trying to slide past the end.
    const nextButton = this.closest('quiz-carousel-component')?.querySelector('[quiz-next-button]');
    nextButton?.click();
  }
}

customElements.get('quiz-email-capture') || customElements.define('quiz-email-capture', QuizEmailCapture);
